/* @flow */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'
import sortBy from 'lodash/sortBy'

import ActionButton from '../../../infrastructure/components/ActionButton'
import FormatCurrency from '../../../infrastructure/components/FormatCurrency'
import {
  ControlLabel,
  FormControl,
  FormGroup,
} from '../../../infrastructure/components/Forms'
import ImageDownloadTransformations from './ImageDownloadTransformations'
import { downloadOrderProductImages } from '../api'
import { getRetailerOrdersList } from '../../orders/api'

import { msg } from '../../shared'
import type { Dispatch, Id, Order } from '../../types'
import { renderDateTime } from '../../shared'

type Props = {
  dispatch: Dispatch,
  onHide: Function,
  orderIds?: Array<Id>,
  show: Id | false,
}

type State = {
  checked: Array<Id>,
  format: 'jpg' | 'png',
  isLoaded: boolean,
  orders: Array<Order>,
  transformations: Array<{
    height: number | 'auto',
    width: number | 'auto',
    format: 'png' | 'jpg',
  }>,
}

class FileDownloader extends Component<Props, State> {
  state = {
    checked: [],
    format: 'jpg',
    isLoaded: false,
    orders: [],
    transformations: [],
  }

  componentDidMount() {
    // Opening without a pre-selected order (e.g. when retailers open it on their dashboard)
    if (!this.props.orderIds) {
      getRetailerOrdersList({
        sort: [{ key: 'date', direction: 'desc' }],
      }).then(response => {
        if (!response.error) {
          this.setState({
            isLoaded: true,
            orders: response.payload.orders,
          })
        }
      })
      // Opening with a pre-selected order, e.g. inside an order view
    } else {
      this.setState({
        checked: this.props.orderIds,
        isLoaded: true,
      })
    }
  }

  render() {
    const { onHide, orderIds, show } = this.props
    const { checked, format, isLoaded, orders, transformations } = this.state

    if (!isLoaded) {
      return null
    }

    return (
      <Modal show={show !== false} onHide={onHide} bsSize="large">
        <Modal.Header>
          <Modal.Title>Download Product Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!orderIds && (
            <>
              <Introduction>
                <p>
                  Start by selecting the orders for which you want to download
                  images. We will prepare an .zip file will all the images of
                  all the order's products and e-mail it to you.
                </p>
              </Introduction>
              <TableContainer>
                <table className="table table-condensed">
                  <thead>
                    <tr>
                      <td />
                      <td>Date</td>
                      <td>No.</td>
                      <td>Brand</td>
                      <td>Total Quantity</td>
                      <td>Total Price</td>
                    </tr>
                  </thead>
                  <tbody>
                    {orders
                      .filter(order => order.brand_id === show)
                      .map(order => {
                        return (
                          <tr>
                            <td className="listview-action">
                              <div className="checkbox check-success">
                                <input
                                  onChange={() => this._toggleOrder(order)}
                                  type="checkbox"
                                  id={`order-image-downloader-${order.order_id}`}
                                  checked={
                                    checked.indexOf(order.order_id) !== -1
                                  }
                                />
                                <label
                                  htmlFor={`order-image-downloader-${order.order_id}`}
                                />
                              </div>
                            </td>
                            <td>{renderDateTime(order.approved_at)}</td>
                            <td>{order.order_number}</td>
                            <td>{order.brand_name}</td>
                            <td>{order.total_quantity}</td>
                            <td>
                              <FormatCurrency currency={order.currency}>
                                {order.net_price}
                              </FormatCurrency>
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </TableContainer>
            </>
          )}
          {checked.length > 0 && (
            <SettingsContainer style={{ marginTop: orderIds ? -25 : 0 }}>
              <div>
                <Header>Select what sizes you want</Header>

                <ImageDownloadTransformations
                  onChange={this._onChangeTransformations}
                  transformations={transformations}
                />
              </div>
            </SettingsContainer>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-white"
            onClick={() => onHide()}
          >
            Cancel
          </button>
          <ActionButton
            className="btn btn-success"
            onClick={() => this._download()}
          >
            Download Product Images{' '}
            {checked.length > 0 ? `for ${checked.length} Orders` : ''}
          </ActionButton>
        </Modal.Footer>
      </Modal>
    )
  }

  _download = () => {
    const { onHide } = this.props
    const { checked, format, transformations } = this.state

    return downloadOrderProductImages(checked, format, transformations).then(
      response => {
        if (!response.error) {
          msg(
            'success',
            'We are preparing your images. An email with a link to download the images will be sent to your e-mail shortly.'
          )

          onHide()
        }
      }
    )
  }

  _onChangeTransformations = transformations => {
    this.setState({
      transformations,
    })
  }

  _toggleOrder = order => {
    const current = [...this.state.checked]
    const index = current.indexOf(order.order_id)

    if (index === -1) {
      current.push(order.order_id)
    } else {
      current.splice(index, 1)
    }

    this.setState(s => ({
      checked: current,
    }))
  }
}

export default connect()(FileDownloader)

const TableContainer = styled.div`
  border-bottom: 1px solid #ccc;
  margin-bottom: 20px;
  paddin-bottom: 10px;
`

const Header = styled.div`
  color: black;
  font-size: 18px;
  margin-bottom: 10px;
`

const SettingsContainer = styled.div`
  > div {
    margin-top: 25px;
  }
`

const Introduction = styled.div.attrs({ className: 'alert alert-info ' })`
  margin-bottom: 20px;
`
